import { useContext, useLayoutEffect, useRef, useState } from "react"
import { useMotionValue, useTransform } from "framer-motion"
import { useLocale } from "contexts/Locale"

import * as lunar from "./lunar"

export const PX_RATIO = 5000000

function detectTouchscreen() {
    var result = false
    if (window.PointerEvent && ('maxTouchPoints' in navigator)) {
        // if Pointer Events are supported, just check maxTouchPoints
        if (navigator.maxTouchPoints > 0) {
            result = true
        }
    } else {
        // no Pointer Events...
        if (window.matchMedia && window.matchMedia("(any-pointer:coarse)").matches) {
            // check for any-pointer:coarse which mostly means touchscreen
            result = true
        } else if (window.TouchEvent || ('ontouchstart' in window)) {
            // last resort - check for exposed touch events API / event handler
            result = true
        }
    }
    return result
}

export function getHtmlDir() {
    if (!process.browser) return 0
    const rtl = document.querySelector("html").getAttribute("dir") === "rtl"
    return 2 * !rtl - 1
}

export function observeResize(el, onResize) {
    if (!el) return
    const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
            const { target, contentBoxSize } = entry
            if (contentBoxSize) {
                const { inlineSize: isize, blockSize: bsize } = entry.contentBoxSize[0]
                onResize({ isize, bsize, target })
            }
        }
    })
    resizeObserver.observe(el)

    return () => {
        resizeObserver.disconnect()
    }
}

export function rafDelay(fn = null) {
    if (!process.browser || !fn) return
    window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
            fn()
        })
    })
}

function phaseFromTime(v) {
    const phase = lunar.moonPhase(new Date(v))
    const offset = phase.age < lunar.SYNODIC_MONTH / 2
    let illu = phase.illumination
    illu = offset ? 2 - illu : illu
    return illu
}

function phaseFromTimeInDays(v) {
    // v est un timestamp en ms (ex: Date.now())
    const date = new Date(v)

    const prevNewMoon = lunar.lastNewMoonBefore(date)
    const nextNewMoon = lunar.nextNewMoonAfter(date)
    const exactFullMoon = lunar.lastFullMoonBefore(nextNewMoon)

    const tPrev = prevNewMoon.getTime()
    const tNext = nextNewMoon.getTime()
    const tFull = exactFullMoon.getTime()

    if (v < tFull) {
        const progress = (v - tPrev) / (tFull - tPrev)
        return 2 - progress
    } else {
        const progress = (v - tFull) / (tNext - tFull)
        return 1 - progress
    }
}

export function convertToIndianNumerals(value, langs, locale) {
    // Vérification de la langue
    if (!langs.split(",").includes(locale)) return value

    // Retourne vide si la valeur est nulle, indéfinie ou vide
    if (value === null || value === undefined || value === '') return ''

    // On force le système de numérotation arabe oriental (arab) via Intl
    const formatter = new Intl.NumberFormat('ar-EG-u-nu-arab', {
        useGrouping: false
    })

    // On convertit la valeur en string pour pouvoir utiliser .replace()
    const stringValue = String(value)

    // [0-9] cible chaque chiffre un par un dans le texte
    return stringValue.replace(/[0-9]/g, (match) => {
        // match est une string (ex: "2"), on la convertit en nombre pour le formatter
        return formatter.format(Number(match))
    })
}

export function MoonCalendarProvider(props) {
    const { children, Ctx } = props
    const locale = useLocale()
    const [touchenabled, setTouchenabled] = useState(-1)
    const ready = useMotionValue(0)
    const start = useMotionValue(0)
    const playing = useMotionValue(0)
    const moving = useMotionValue(Date.now())
    const loaded = useMotionValue(0)
    const ended = useMotionValue(0)
    const focused = useMotionValue(0)
    const px_ratio = useRef(0)
    const px_ratio_p = useRef(0)
    const moon_time = useMotionValue(Date.now())
    const moon_phase = useTransform(moon_time, phaseFromTimeInDays)
    const moon_illu = useTransform(moon_time, phaseFromTime)
    const moon_phase_label = useTransform(moon_phase, v => {
        // Une marge de 0.035 correspond à environ 12 heures avant et 12 heures après l'événement
        const margin = 0.035

        // 1. Les 4 phases principales "élargies"
        if (v < margin || v > 2 - margin) return "new_moon"
        if (Math.abs(v - 1.5) <= margin) return "first_quarter"
        if (Math.abs(v - 1.0) <= margin) return "full_moon"
        if (Math.abs(v - 0.5) <= margin) return "third_quarter"

        // 2. Les phases intermédiaires
        if (v > 1.5) return "waxing_crescent"
        if (v > 1.0) return "waxing_gibbous"
        if (v > 0.5) return "waning_gibbous"
        return "waning_crescent"
    })

    function lastFM() {
        return lunar.lastFullMoonBefore(new Date(now())).getTime()
    }
    function nextFM() {
        return lunar.nextFullMoonAfter(new Date(now())).getTime()
    }
    function now() {
        return Date.now()
    }

    useLayoutEffect(() => {
        setTouchenabled(+(detectTouchscreen() && !window.matchMedia('(hover:hover)').matches))
    }, [])

    return touchenabled >= 0 ? (
        <Ctx.Provider
            value={{
                touchenabled,
                ready,
                loaded,
                start,
                playing,
                moving,
                ended,
                focused,
                moon_time,
                moon_phase,
                moon_illu,
                moon_phase_label,
                px_ratio,
                px_ratio_p,
                lastFM,
                nextFM,
                now,
                locale: locale?.locale?.codes?.ws,
            }}
        >
            {children}
        </Ctx.Provider>
    ) : null
}

export function useMoonCalendar(ctx) {
    return useContext(ctx)
}
